/**
 * How a person's own status resolves, and what picking a status does, shared
 * by the web account menu (hooks/usePresence) and the native one, so the two
 * cannot disagree about what someone is shown as.
 */
import type { ManualPresenceEntry, PresenceStatus, ResolvedPresenceStatus } from '../types/presence';

/** What /presence/users reports for one person; only the fields that matter here. */
export interface ServerPresenceSummary {
  state?: ResolvedPresenceStatus | null;
  source?: string;
}

export interface OwnPresenceFacts {
  manual: ManualPresenceEntry | null;
  /** Automatic status from the server: in a meeting, on site, out of office. */
  serverAutoState: ResolvedPresenceStatus | null;
  callStatus?: string;
  callAvailability?: string;
  inactive?: boolean;
  outsideWorkingHours?: boolean;
  now?: Date;
}

const LIVE_CALL_STATUSES = new Set(['connected', 'outgoing', 'voicemail', 'incoming']);

/** False once a timed status has run out, so an expired pick reads as automatic everywhere. */
export function isManualEntryLive(entry: ManualPresenceEntry | null | undefined, now: Date = new Date()): entry is ManualPresenceEntry {
  if (!entry) return false;
  return !entry.expiresAt || new Date(entry.expiresAt).getTime() > now.getTime();
}

/** The server statuses that stand in for "automatic" in the account menu. */
export function serverAutoPresence(presence: ServerPresenceSummary | null | undefined): ResolvedPresenceStatus | null {
  const source = presence?.source;
  return source === 'out_of_office' || source === 'meeting' || source === 'on_site' ? presence?.state ?? null : null;
}

/**
 * Priority chain: a live call, the manual pick, the server's automatic status,
 * Do Not Disturb from calls, inactivity, after hours, then available.
 */
export function resolveOwnPresence(facts: OwnPresenceFacts): ResolvedPresenceStatus {
  if (facts.callStatus && LIVE_CALL_STATUSES.has(facts.callStatus)) return 'busy';
  if (isManualEntryLive(facts.manual, facts.now)) return facts.manual.status;
  if (facts.serverAutoState) return facts.serverAutoState;
  if (facts.callAvailability === 'dnd') return 'dnd';
  if (facts.inactive) return 'away';
  if (facts.outsideWorkingHours) return 'away';
  return 'available';
}

/** Picking the status already set by hand clears it, as "Reset to automatic" does. */
export function nextManualPresence(
  picked: PresenceStatus,
  current: ResolvedPresenceStatus,
  source: 'auto' | 'manual',
): PresenceStatus | null {
  return picked === current && source === 'manual' ? null : picked;
}

export function manualPresenceEntry(status: PresenceStatus | null, expiresAt?: Date, now: Date = new Date()): ManualPresenceEntry | null {
  return status ? { status, expiresAt: expiresAt?.toISOString() ?? null, setAt: now.toISOString() } : null;
}
